import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonComponent, ConfirmDialogService, ToastService } from '@dental-ui/ui';
import {
  LucideChevronLeft,
  LucideChevronRight,
  LucidePencil,
  LucidePlus,
  LucideRefreshCw,
  LucideSearch,
  LucideTrash2,
  LucideX,
} from '@lucide/angular';
import { BrandDto, PortalBrandsApiService } from '../data-access/portal-brands-api.service';

const PAGE_SIZE = 10;

@Component({
  selector: 'lib-portal-brands',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonComponent,
    LucideChevronLeft,
    LucideChevronRight,
    LucidePencil,
    LucidePlus,
    LucideRefreshCw,
    LucideSearch,
    LucideTrash2,
    LucideX,
  ],
  templateUrl: './portal-brands.component.html',
  styleUrl: './portal-brands.component.scss',
})
export class PortalBrandsComponent implements OnInit {
  private readonly api = inject(PortalBrandsApiService);
  private readonly toast = inject(ToastService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly items = signal<BrandDto[]>([]);
  readonly loading = signal(false);
  readonly totalPages = signal(0);
  readonly page = signal(0);
  searchTerm = '';

  readonly showFormDialog = signal(false);
  readonly formSaving = signal(false);
  readonly isEdit = signal(false);
  readonly formModel = signal<BrandDto>({ id: '', name: '', isActive: true });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.getBrands(this.page(), PAGE_SIZE, this.searchTerm).subscribe({
      next: (res) => {
        this.items.set(res.data);
        this.totalPages.set(res.totalPage);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.toast.error('Markalar yüklenirken hata oluştu');
      },
    });
  }

  onSearch(): void {
    this.page.set(0);
    this.load();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.page.set(0);
    this.load();
  }

  prevPage(): void {
    if (this.page() > 0) {
      this.page.update((p) => p - 1);
      this.load();
    }
  }

  nextPage(): void {
    if (this.page() < this.totalPages() - 1) {
      this.page.update((p) => p + 1);
      this.load();
    }
  }

  openAdd(): void {
    this.isEdit.set(false);
    this.formModel.set({ id: '', name: '', isActive: true });
    this.showFormDialog.set(true);
  }

  openEdit(item: BrandDto): void {
    this.isEdit.set(true);
    this.formModel.set({ ...item });
    this.showFormDialog.set(true);
  }

  closeFormDialog(): void {
    this.showFormDialog.set(false);
  }

  updateField<K extends keyof BrandDto>(key: K, value: BrandDto[K]): void {
    this.formModel.update((prev) => ({ ...prev, [key]: value }));
  }

  save(): void {
    const model = this.formModel();
    if (!model.name.trim()) {
      this.toast.error('Ad alanı zorunludur');
      return;
    }

    this.formSaving.set(true);
    const call = this.isEdit() ? this.api.updateBrand(model) : this.api.createBrand(model);

    call.subscribe({
      next: () => {
        this.formSaving.set(false);
        this.toast.success(this.isEdit() ? 'Marka güncellendi' : 'Marka eklendi');
        this.showFormDialog.set(false);
        this.load();
      },
      error: () => {
        this.formSaving.set(false);
        this.toast.error('İşlem sırasında hata oluştu');
      },
    });
  }

  delete(item: BrandDto): void {
    this.confirmDialog
      .confirm({
        title: 'Markayı Sil',
        message: `"${item.name}" kaydını silmek istediğinizden emin misiniz?`,
        confirmText: 'Sil',
        cancelText: 'İptal',
        confirmButtonType: 'danger',
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.api.deleteBrand(item.id).subscribe({
          next: () => {
            this.toast.success('Marka silindi');
            this.load();
          },
          error: () => this.toast.error('Silme işlemi başarısız'),
        });
      });
  }
}
